<template>
  <div class="ai-agent-form">
    <div class="">
      <label class="block mb-2">AI Agent</label>
      <div
        class="w-full"
        v-tooltip.top="{
          value: 'The load must be unposted and saved before the AI Agent type can be changed.',
          class: 'tooltip-features',
          disabled: !aIAgentLocked,
        }"
      >
        <Dropdown
          v-model="aIAgentTypeID"
          :options="aIAgents"
          optionLabel="Name"
          optionValue="ID"
          placeholder="Select"
          class="w-full"
          :disabled="aIAgentLocked"
        />
      </div>
    </div>

    <!-- LaneSurf specific fields -->
    <div v-if="aIAgentTypeID === constants.AIAgent.LaneSurf">
      <h3 class="section-heading">Details for LaneSurf:</h3>
      <div class="mb-3">
        <label class="block mb-2">Rate</label>
        <div class="field-group">
          <div class="field-item">
            <label class="field-label">Posted</label>
            <InputNumber
              v-model="laneSurf.PostedRate"
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="$0.00"
              :class="['field-input-number', { 'error': errors.laneSurf.PostedRate }]"
            />
          </div>
          <div class="field-item">
            <label class="field-label">Max</label>
            <InputNumber
              v-model="laneSurf.MaxRate"
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="$0.00"
              :class="['field-input-number', { 'error': errors.laneSurf.MaxRate }]"
            />
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="block mb-2">Dimensions (in.)</label>
        <div class="field-group">
          <div class="field-item">
            <label class="field-label">Length</label>
            <InputNumber
              v-model="laneSurf.Length"
              :useGrouping="false"
              placeholder="0"
              :class="['field-input-number', { 'error': errors.laneSurf.Length }]"
            />
          </div>
          <div class="field-item">
            <label class="field-label">Width</label>
            <InputNumber
              v-model="laneSurf.Width"
              :useGrouping="false"
              placeholder="0"
              :class="['field-input-number', { 'error': errors.laneSurf.Width }]"
            />
          </div>
          <div class="field-item">
            <label class="field-label">Height</label>
            <InputNumber
              v-model="laneSurf.Height"
              :useGrouping="false"
              placeholder="0"
              :class="['field-input-number', { 'error': errors.laneSurf.Height }]"
            />
          </div>
        </div>
      </div>

      <div class="mb-3">
        <div class="field-group">
          <div class="field-item equipment-type-col">
            <label class="block mb-2">Equipment Type</label>
          </div>
          <div class="field-item equipment-length-col">
            <label class="block mb-2">Length</label>
          </div>
          <div class="equipment-delete-col"></div>
        </div>
        <div v-for="(row, index) in laneSurf.Equipment" :key="index" class="field-group equipment-row">
          <div class="field-item equipment-type-col">
            <Dropdown
              v-model="row.type"
              :options="equipmentTypeOptions"
              optionLabel="value"
              optionValue="id"
              placeholder="Select"
              :class="['w-full', { 'error-dropdown': errors.laneSurf.Equipment[index] }]"
            />
          </div>
          <div class="field-item equipment-length-col">
            <InputNumber
              v-model="row.length"
              :useGrouping="false"
              placeholder="0"
              :class="['field-input-number', { 'error': errors.laneSurf.Equipment[index] }]"
            />
          </div>
          <div class="equipment-delete-col">
            <button
              type="button"
              class="delete-equipment-btn"
              title="Remove equipment type"
              @click="removeEquipmentRow(index)"
            >
              <i class="pi pi-trash"></i>
            </button>
          </div>
        </div>
        <button type="button" class="add-equipment-btn" @click="addEquipmentRow">
          Add
        </button>
      </div>

      <div class="mb-3">
        <div class="override-checkbox-row">
          <Checkbox inputId="overrideAppointments" v-model="overrideAppointments" :binary="true" />
          <label for="overrideAppointments">Override appointments</label>
        </div>
      </div>

      <div class="mb-3 appointment-section" v-if="laneSurf.PickupAppointments.length">
        <h4 class="appointment-section-heading pickup-heading">Pickup Appointment<span v-if="laneSurf.PickupAppointments.length > 1">s</span></h4>
        <div
          class="appointment-row"
          v-for="(row, index) in laneSurf.PickupAppointments"
          :key="'pickup-' + (row.locationID ?? index)"
        >
        <label class="block mb-2" v-if="row.label || laneSurf.PickupAppointments.length > 1">{{ row.label || ('Stop ' + (index + 1)) }}</label>
        <div class="field-group">
          <div class="field-item">
            <label class="field-label">Start</label>
            <div class="datetime-group">
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'StartDate')"
                  @update:modelValue="(val) => setAppointmentField(row, 'StartDate', val)"
                  :disabled="!overrideAppointments"
                  :showOtherMonth="true"
                  :selectOtherMonths="true"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  dateFormat="mm/dd/yy"
                  :showOnFocus="false"
                  @keypress="isNumberSlash($event)"
                  @blur="handleDateBlur($event, row, 'StartDate')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('PickupAppointments', index, 'StartDate') }"
                />
              </div>
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'StartTime')"
                  @update:modelValue="(val) => setAppointmentField(row, 'StartTime', val)"
                  :disabled="!overrideAppointments"
                  showIcon
                  timeOnly
                  hourFormat="24"
                  placeholder="HH:MM"
                  :showOnFocus="false"
                  icon="pi pi-clock"
                  @keypress="isNumberColon($event)"
                  @blur="handleTimeBlur($event, row, 'StartTime')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('PickupAppointments', index, 'StartTime') }"
                />
              </div>
            </div>
          </div>
          <div class="field-item">
            <label class="field-label">End</label>
            <div class="datetime-group">
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'EndDate')"
                  @update:modelValue="(val) => setAppointmentField(row, 'EndDate', val)"
                  :disabled="!overrideAppointments"
                  :showOtherMonth="true"
                  :selectOtherMonths="true"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  dateFormat="mm/dd/yy"
                  :showOnFocus="false"
                  @keypress="isNumberSlash($event)"
                  @blur="handleDateBlur($event, row, 'EndDate')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('PickupAppointments', index, 'EndDate') }"
                />
              </div>
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'EndTime')"
                  @update:modelValue="(val) => setAppointmentField(row, 'EndTime', val)"
                  :disabled="!overrideAppointments"
                  showIcon
                  timeOnly
                  hourFormat="24"
                  placeholder="HH:MM"
                  :showOnFocus="false"
                  icon="pi pi-clock"
                  @keypress="isNumberColon($event)"
                  @blur="handleTimeBlur($event, row, 'EndTime')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('PickupAppointments', index, 'EndTime') }"
                />
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div class="mb-3 appointment-section" v-if="laneSurf.DeliveryAppointments.length">
        <h4 class="appointment-section-heading drop-heading">Drop Appointment<span v-if="laneSurf.DeliveryAppointments.length > 1">s</span></h4>
        <div
          class="appointment-row"
          v-for="(row, index) in laneSurf.DeliveryAppointments"
          :key="'delivery-' + (row.locationID ?? index)"
        >
        <label class="block mb-2" v-if="row.label || laneSurf.DeliveryAppointments.length > 1">{{ row.label || ('Stop ' + (index + 1)) }}</label>
        <div class="field-group">
          <div class="field-item">
            <label class="field-label">Start</label>
            <div class="datetime-group">
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'StartDate')"
                  @update:modelValue="(val) => setAppointmentField(row, 'StartDate', val)"
                  :disabled="!overrideAppointments"
                  :showOtherMonth="true"
                  :selectOtherMonths="true"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  dateFormat="mm/dd/yy"
                  :showOnFocus="false"
                  @keypress="isNumberSlash($event)"
                  @blur="handleDateBlur($event, row, 'StartDate')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('DeliveryAppointments', index, 'StartDate') }"
                />
              </div>
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'StartTime')"
                  @update:modelValue="(val) => setAppointmentField(row, 'StartTime', val)"
                  :disabled="!overrideAppointments"
                  showIcon
                  timeOnly
                  hourFormat="24"
                  placeholder="HH:MM"
                  :showOnFocus="false"
                  icon="pi pi-clock"
                  @keypress="isNumberColon($event)"
                  @blur="handleTimeBlur($event, row, 'StartTime')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('DeliveryAppointments', index, 'StartTime') }"
                />
              </div>
            </div>
          </div>
          <div class="field-item">
            <label class="field-label">End</label>
            <div class="datetime-group">
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'EndDate')"
                  @update:modelValue="(val) => setAppointmentField(row, 'EndDate', val)"
                  :disabled="!overrideAppointments"
                  :showOtherMonth="true"
                  :selectOtherMonths="true"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  dateFormat="mm/dd/yy"
                  :showOnFocus="false"
                  @keypress="isNumberSlash($event)"
                  @blur="handleDateBlur($event, row, 'EndDate')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('DeliveryAppointments', index, 'EndDate') }"
                />
              </div>
              <div class="field-tooltip-wrapper">
                <Calendar
                  :modelValue="appointmentFieldDisplay(row, 'EndTime')"
                  @update:modelValue="(val) => setAppointmentField(row, 'EndTime', val)"
                  :disabled="!overrideAppointments"
                  showIcon
                  timeOnly
                  hourFormat="24"
                  placeholder="HH:MM"
                  :showOnFocus="false"
                  icon="pi pi-clock"
                  @keypress="isNumberColon($event)"
                  @blur="handleTimeBlur($event, row, 'EndTime')"
                  autocomplete="off"
                  :class="{ error: hasAppointmentError('DeliveryAppointments', index, 'EndTime') }"
                />
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="block mb-2">Special Instructions</label>
        <textarea
          v-model="laneSurf.SpecialInstructions"
          maxlength="2000"
          rows="4"
          placeholder="Enter special instructions..."
          class="w-full field-input"
        ></textarea>
      </div>
    </div>
    <vue-element-loading
      :active="aIAgentLoading"
      spinner="bar-fade-scale"
      color="#FF6700"
      :is-full-screen="false"
    />
  </div>
</template>

<script>
import { useAIAgent } from "@/components/common/AIAgent/aIAgent.js";
import constants from "@/assets/constants.js";
import { loadEquipmentList } from "@/assets/loadEquipmentList.js";
import DateHelper from "@/assets/datehelpers";
import CustomerService from "@/service/CustomerService.js";
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import Calendar from "primevue/calendar";
import Checkbox from "primevue/checkbox";
import VueElementLoading from "vue-element-loading";

function formatLocalDateTime(date) {
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

function combineDateAndTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;

  // Convert dateValue to Date object if it's a string
  let dateObj;
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    dateObj = dateValue;
  } else if (typeof dateValue === 'string') {
    dateObj = new Date(dateValue);
    if (isNaN(dateObj.getTime())) return null;
  } else {
    return null;
  }

  // Parse time from either Date object or string
  let hours, minutes;
  if (timeValue instanceof Date && !isNaN(timeValue.getTime())) {
    // timeValue is a Date object
    hours = timeValue.getHours();
    minutes = timeValue.getMinutes();
  } else if (typeof timeValue === 'string') {
    // timeValue is a string in "HH:MM" format
    const timeParts = timeValue.split(':');
    if (timeParts.length !== 2) return null;
    hours = parseInt(timeParts[0], 10);
    minutes = parseInt(timeParts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;
  } else {
    return null;
  }

  return new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
    hours,
    minutes
  );
}

function normalizeDateValue(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return new Date(value.getTime());
  if (typeof value === "string") {
    const parsedDate = new Date(value);
    if (!isNaN(parsedDate.getTime())) return parsedDate;
  }
  return null;
}

function normalizeTimeValue(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return new Date(value.getTime());

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    const timeOnlyMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (timeOnlyMatch) {
      let hours = parseInt(timeOnlyMatch[1], 10);
      const minutes = parseInt(timeOnlyMatch[2], 10);
      const suffix = timeOnlyMatch[4]?.toUpperCase();

      if (suffix === "PM" && hours < 12) hours += 12;
      if (suffix === "AM" && hours === 12) hours = 0;

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return new Date(1970, 0, 1, hours, minutes, 0, 0);
      }
    }

    const digitsOnlyMatch = trimmedValue.match(/^(\d{1,4})$/);
    if (digitsOnlyMatch) {
      const digits = digitsOnlyMatch[1];
      const hours = parseInt(digits.length <= 2 ? digits : digits.slice(0, -2), 10);
      const minutes = digits.length <= 2 ? 0 : parseInt(digits.slice(-2), 10);

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return new Date(1970, 0, 1, hours, minutes, 0, 0);
      }
    }
  }

  return null;
}

function normalizeAppointmentField(field, value) {
  return field.includes('Date') ? normalizeDateValue(value) : normalizeTimeValue(value);
}

function createLaneSurfDefaults() {
  return {
    PostedRate: null,
    MaxRate: null,
    Length: null,
    Width: null,
    Height: null,
    SpecialInstructions: '',
    Equipment: [],
    PickupAppointments: [],
    DeliveryAppointments: []
  };
}

// A row pairs the load's own (read-only) appointment info for one stop - shown whenever
// "Override appointments" is off - with the editable override values that get sent when
// it's on. `overrideValues` carries over an in-progress edit or a previously saved
// override; anything it doesn't specify starts out empty.
function buildAppointmentRow(defaultRow, overrideValues) {
  return {
    locationID: defaultRow?.locationID ?? null,
    label: defaultRow?.label ?? '',
    DefaultStartDate: defaultRow?.StartDate ?? null,
    DefaultStartTime: defaultRow?.StartTime ?? null,
    DefaultEndDate: defaultRow?.EndDate ?? null,
    DefaultEndTime: defaultRow?.EndTime ?? null,
    StartDate: overrideValues?.StartDate ?? null,
    StartTime: overrideValues?.StartTime ?? null,
    EndDate: overrideValues?.EndDate ?? null,
    EndTime: overrideValues?.EndTime ?? null
  };
}

// Builds the row list for one stop type (pickup or delivery) from the load's current
// stops (`defaultRows`, one per actual pickup/delivery location), preferring an
// in-progress edit (`existingRows`) over the appointment data the API returned for this
// load (`savedAppointments`) for a given stop.
//
// Appointments are matched to a stop by locationID. A backend that hasn't been updated
// yet to send/accept locationID on each appointment falls back to positional matching,
// which preserves today's single-pickup/single-delivery behavior until it is.
function mergeAppointmentRows(defaultRows, savedAppointments, existingRows, stopTypeLower) {
  const existingByLocationID = new Map(
    (existingRows || []).filter(row => row.locationID != null).map(row => [row.locationID, row])
  );

  const savedForType = (savedAppointments || []).filter(
    (a) => (a.stopType ?? a.StopType ?? '').toLowerCase() === stopTypeLower
  );
  const savedByLocationID = new Map(
    savedForType
      .filter((a) => (a.locationID ?? a.LocationID ?? null) !== null)
      .map((a) => [a.locationID ?? a.LocationID, a])
  );
  const savedWithoutLocationID = savedForType.filter((a) => (a.locationID ?? a.LocationID ?? null) === null);

  return (defaultRows || []).map((defaultRow, index) => {
    const existing = (defaultRow.locationID != null && existingByLocationID.get(defaultRow.locationID))
      || existingRows?.[index]
      || null;
    if (existing) {
      return buildAppointmentRow(defaultRow, existing);
    }

    const saved = (defaultRow.locationID != null && savedByLocationID.get(defaultRow.locationID))
      || savedWithoutLocationID[index]
      || null;
    const start = saved ? (saved.appointmentStart ?? saved.AppointmentStart) : null;
    const end = saved ? (saved.appointmentEnd ?? saved.AppointmentEnd) : null;
    return buildAppointmentRow(defaultRow, {
      StartDate: start ? new Date(start) : null,
      StartTime: start ? new Date(start) : null,
      EndDate: end ? new Date(end) : null,
      EndTime: end ? new Date(end) : null
    });
  });
}

export default {
  setup() {
    const {
      aIAgentLoading,
      aIAgentTypeID,
      aIAgentData,
      aIAgents,
      getAIAgentOptions,
      getLoadAIAgentData,
      setLoadAIAgentData
    } = useAIAgent();

    return {
      aIAgentLoading,
      aIAgentTypeID,
      aIAgentData,
      aIAgents,
      getAIAgentOptions,
      getLoadAIAgentData,
      setLoadAIAgentData
    }
  },
  name: 'AIAgentForm',
  components: {
    Dropdown,
    InputNumber,
    Calendar,
    Checkbox,
    VueElementLoading
  },
  props: {
    // { pickups: [{ locationID, label, StartDate, StartTime, EndDate, EndTime }, ...],
    //   deliveries: [...] } - one entry per actual pickup/delivery stop on the load.
    appointmentDefaults: {
      type: Object,
      default: null
    },
    postedRateDefault: {
      type: Number,
      default: null
    }
  },
  emits: ['submit'],
  data() {
    return {
      constants,
      customerService: null,
      // LaneSurf specific fields
      laneSurf: createLaneSurfDefaults(),
      // Raw Appointments array last returned by the API, kept aside so appointment rows
      // can be rebuilt (see rebuildAppointmentLists) without losing that data if
      // appointmentDefaults finishes loading after aIAgentData already arrived.
      savedAppointments: [],
      overrideAppointments: false,
      equipmentTypeOptions: loadEquipmentList,
      // Validation errors
      errors: {
        laneSurf: {
          PostedRate: false,
          MaxRate: false,
          Length: false,
          Width: false,
          Height: false,
          Equipment: [],
          PickupAppointments: [],
          DeliveryAppointments: []
        }
      }
    };
  },
  computed: {
    aIAgentLocked() {
      return this.aIAgents.length === 1;
    },
    getHourFormat() {
      return "24hrs";
    }
  },
  created() {
    this.customerService = new CustomerService();
  },
  mounted() {
    this.disableCalendarTriggerTabbing();
  },
  updated() {
    this.disableCalendarTriggerTabbing();
  },
  watch: {
    'aIAgentData': {
      handler(newVal) {
        if(this.aIAgentTypeID !== constants.AIAgent.LaneSurf) {
          return;
        }

        if (newVal !== null) {
            const data = {...createLaneSurfDefaults(), ...this.aIAgentData};

            // Map equipment type overrides into editable rows
            data.Equipment = (data.Equipment || []).map(e => ({
              type: e.type ?? e.Type ?? null,
              length: e.length ?? e.Length ?? null
            }));

            this.savedAppointments = data.Appointments || [];
            delete data.Appointments;
            data.PickupAppointments = [];
            data.DeliveryAppointments = [];

            this.overrideAppointments = this.savedAppointments.length > 0;
            this.laneSurf = data;
        } else {
          this.overrideAppointments = false;
          this.savedAppointments = [];
          this.laneSurf = createLaneSurfDefaults();
        }
        this.rebuildAppointmentLists();
        this.prefillPostedRateFromPay();
      },
      immediate: true
    },
    'appointmentDefaults': {
      handler() {
        this.rebuildAppointmentLists();
      },
      deep: true
    },
    'overrideAppointments'(newVal) {
      if (newVal) {
        this.prefillAppointmentsFromLoad();
      }
    },
    'postedRateDefault'() {
      this.prefillPostedRateFromPay();
    },
    'laneSurf.PostedRate'(newVal) {
      if (newVal !== null && newVal !== '') {
        this.errors.laneSurf.PostedRate = false;
      }
    },
    'laneSurf.MaxRate'(newVal) {
      if (newVal !== null && newVal !== '') {
        this.errors.laneSurf.MaxRate = false;
      }
    },
    'laneSurf.Length'() {
      this.clearDimensionErrors();
    },
    'laneSurf.Width'() {
      this.clearDimensionErrors();
    },
    'laneSurf.Height'() {
      this.clearDimensionErrors();
    },
    'laneSurf.PickupAppointments': {
      handler() {
        if (this.overrideAppointments) this.validateAppointmentList('PickupAppointments');
      },
      deep: true
    },
    'laneSurf.DeliveryAppointments': {
      handler() {
        if (this.overrideAppointments) this.validateAppointmentList('DeliveryAppointments');
      },
      deep: true
    }
  },
  methods: {
    // The Calendar trigger icon button is focusable by default, which breaks straight
    // field-to-field tabbing (date -> button -> time -> button). It's still clickable.
    disableCalendarTriggerTabbing() {
      this.$el.querySelectorAll('.p-datepicker-trigger').forEach(button => {
        if (button.tabIndex !== -1) button.tabIndex = -1;
      });
    },
    // Rebuilds both appointment row lists from the load's current stops
    // (appointmentDefaults), preferring in-progress edits, then falling back to
    // whatever was last saved for that stop. Called whenever either input changes.
    rebuildAppointmentLists() {
      this.laneSurf.PickupAppointments = mergeAppointmentRows(
        this.appointmentDefaults?.pickups, this.savedAppointments, this.laneSurf.PickupAppointments, 'pickup'
      );
      this.laneSurf.DeliveryAppointments = mergeAppointmentRows(
        this.appointmentDefaults?.deliveries, this.savedAppointments, this.laneSurf.DeliveryAppointments, 'delivery'
      );
    },
    appointmentFieldDisplay(row, field) {
      if (this.overrideAppointments) return normalizeAppointmentField(field, row[field]);
      return normalizeAppointmentField(field, row['Default' + field]);
    },
    setAppointmentField(row, field, value) {
      if (this.overrideAppointments) row[field] = value;
    },
    hasAppointmentError(listName, index, field) {
      const rowErrors = this.errors.laneSurf[listName][index];
      return !!(rowErrors && rowErrors[field]);
    },
    // Give the newly-revealed override fields a starting point from the load's own
    // appointment info. Fields that already hold a value (a previously saved override,
    // or something the user typed before unchecking) are left alone.
    prefillAppointmentsFromLoad() {
      ['PickupAppointments', 'DeliveryAppointments'].forEach(listName => {
        this.laneSurf[listName].forEach(row => {
          ['StartDate', 'StartTime', 'EndDate', 'EndTime'].forEach(field => {
            if (normalizeAppointmentField(field, row[field]) !== null) return;

            const defaultValue = normalizeAppointmentField(field, row['Default' + field]);
            if (defaultValue) row[field] = defaultValue;
          });
        });
      });
    },
    // Give Posted Rate a starting point from the commodity section's Pay field, but only
    // when there's no existing value - a previously saved override, or anything typed in.
    prefillPostedRateFromPay() {
      if (this.laneSurf.PostedRate !== null && this.laneSurf.PostedRate !== '') return;
      if (this.postedRateDefault === null || this.postedRateDefault === undefined) return;

      this.laneSurf.PostedRate = this.postedRateDefault;
    },
    validateAppointmentWindow(row, rowErrors) {
      const { StartDate, StartTime, EndDate, EndTime } = row;

      // clear out all errors
      rowErrors.StartDate = false;
      rowErrors.StartTime = false;
      rowErrors.EndDate = false;
      rowErrors.EndTime = false;

      const anyFilled = !!(StartDate || StartTime || EndDate || EndTime);
      if (!anyFilled) return true;

      let valid = true;

      if (!StartDate) {
        rowErrors.StartDate = true;
        valid = false;
      }
      if (!StartTime) {
        rowErrors.StartTime = true;
        valid = false;
      }
      if (!EndDate) {
        rowErrors.EndDate = true;
        valid = false;
      }
      if (!EndTime) {
        rowErrors.EndTime = true;
        valid = false;
      }

      if (valid) {
        const startDT = combineDateAndTime(StartDate, StartTime);
        const endDT = combineDateAndTime(EndDate, EndTime);
        if (endDT < startDT) {
          rowErrors.EndDate = true;
          rowErrors.EndTime = true;
          valid = false;
        }
      }

      return valid;
    },
    validateAppointmentList(listName) {
      let isValid = true;
      this.laneSurf[listName].forEach((row, index) => {
        if (!this.errors.laneSurf[listName][index]) {
          this.errors.laneSurf[listName][index] = { StartDate: false, StartTime: false, EndDate: false, EndTime: false };
        }
        if (!this.validateAppointmentWindow(row, this.errors.laneSurf[listName][index])) {
          isValid = false;
        }
      });
      return isValid;
    },
    addEquipmentRow() {
      this.laneSurf.Equipment.push({ type: null, length: null });
    },
    removeEquipmentRow(index) {
      this.laneSurf.Equipment.splice(index, 1);
      this.errors.laneSurf.Equipment.splice(index, 1);
    },
    isNumberSlash(evt) {
      evt = evt ? evt : window.event;
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '/'];
      var textInField = evt.key;

      if (textInField.length == 1) {
        if (!keysAllowed.includes(textInField)) {
          evt.preventDefault();
        }
      }

      if (textInField !== '/') {
        DateHelper.AutoAddSlashes(evt);
      }
    },
    handleDateBlur(evt, row, field) {
      if (!this.overrideAppointments) return;
      row[field] = DateHelper.formatDate(evt);
    },
    isNumberColon(evt) {
      evt = evt ? evt : window.event;
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':'];
      var textInField = evt.key;

      if (textInField.length == 1) {
        if (!keysAllowed.includes(textInField)) {
          evt.preventDefault();
        }
      }
    },
    handleTimeBlur(evt, row, field) {
      if (!this.overrideAppointments) return;
      let time = this.customerService.TimeValidation(evt.value, this.getHourFormat);
      row[field] = !time || time.includes("Invalid date") ? null : time;
    },
    clearDimensionErrors() {
      const hasLength = this.laneSurf.Length !== null && this.laneSurf.Length !== '';
      const hasWidth = this.laneSurf.Width !== null && this.laneSurf.Width !== '';
      const hasHeight = this.laneSurf.Height !== null && this.laneSurf.Height !== '';

      // If all three are empty, clear all errors
      if (!hasLength && !hasWidth && !hasHeight) {
        this.errors.laneSurf.Length = false;
        this.errors.laneSurf.Width = false;
        this.errors.laneSurf.Height = false;
      } else {
        // Clear error for fields that have values
        if (hasLength) this.errors.laneSurf.Length = false;
        if (hasWidth) this.errors.laneSurf.Width = false;
        if (hasHeight) this.errors.laneSurf.Height = false;
      }
    },
    validateLaneSurfFields() {
      // Reset errors
      this.errors.laneSurf = {
        PostedRate: false,
        MaxRate: false,
        Length: false,
        Width: false,
        Height: false,
        Equipment: this.laneSurf.Equipment.map(() => false),
        PickupAppointments: this.laneSurf.PickupAppointments.map(() => ({ StartDate: false, StartTime: false, EndDate: false, EndTime: false })),
        DeliveryAppointments: this.laneSurf.DeliveryAppointments.map(() => ({ StartDate: false, StartTime: false, EndDate: false, EndTime: false }))
      };

      let isValid = true;

      // Validate dimensions: if any one is filled, all three are required
      const hasLength = this.laneSurf.Length !== null && this.laneSurf.Length !== '';
      const hasWidth = this.laneSurf.Width !== null && this.laneSurf.Width !== '';
      const hasHeight = this.laneSurf.Height !== null && this.laneSurf.Height !== '';
      const anyDimensionFilled = hasLength || hasWidth || hasHeight;

      if (anyDimensionFilled) {
        if (!hasLength) {
          this.errors.laneSurf.Length = true;
          isValid = false;
        }
        if (!hasWidth) {
          this.errors.laneSurf.Width = true;
          isValid = false;
        }
        if (!hasHeight) {
          this.errors.laneSurf.Height = true;
          isValid = false;
        }
      }

      // Equipment rows are optional overrides, but a non-empty row needs both type and length
      this.laneSurf.Equipment.forEach((row, index) => {
        const hasType = row.type !== null && row.type !== '';
        const hasRowLength = row.length !== null && row.length !== '';
        if ((hasType || hasRowLength) && !(hasType && hasRowLength)) {
          this.errors.laneSurf.Equipment[index] = true;
          isValid = false;
        }
      });

      // Pickup and delivery appointment windows only apply when overriding: all 4
      // sub-fields are required, must be validly formatted, and end must not be before start
      if (this.overrideAppointments) {
        this.laneSurf.PickupAppointments.forEach((row, index) => {
          if (!this.validateAppointmentWindow(row, this.errors.laneSurf.PickupAppointments[index])) {
            isValid = false;
          }
        });
        this.laneSurf.DeliveryAppointments.forEach((row, index) => {
          if (!this.validateAppointmentWindow(row, this.errors.laneSurf.DeliveryAppointments[index])) {
            isValid = false;
          }
        });
      }

      return isValid;
    },
    validate() {
      // If LaneSurf is selected, validate
      if (this.aIAgentTypeID === this.constants.AIAgent.LaneSurf) {
        return this.validateLaneSurfFields();
      }
      return true;
    },
    submit() {
      if (this.validate()) {
        // Save data for LaneSurf
        if (this.aIAgentTypeID === this.constants.AIAgent.LaneSurf) {
          this.setLoadAIAgentData(this.buildLaneSurfPayload());
        }
        this.$emit('submit');
        return true;
      }
      return false;
    },
    buildLaneSurfPayload() {
      const payload = {
        PostedRate: (this.laneSurf.PostedRate === null || this.laneSurf.PostedRate === '') ? 0 : this.laneSurf.PostedRate,
        MaxRate: (this.laneSurf.MaxRate === null || this.laneSurf.MaxRate === '') ? 0 : this.laneSurf.MaxRate,
        Length: this.laneSurf.Length,
        Width: this.laneSurf.Width,
        Height: this.laneSurf.Height,
        SpecialInstructions: this.laneSurf.SpecialInstructions,
        Equipment: this.laneSurf.Equipment
          .filter(row => row.type !== null && row.type !== '')
          .map(row => ({ type: row.type, length: row.length })),
        Appointments: []
      };

      // Only send appointment overrides when the user has opted into overriding them;
      // otherwise the load's own appointment times apply and nothing is sent.
      if (this.overrideAppointments) {
        // Serialize as timezone-less local datetimes. locationID identifies which stop
        // this override belongs to when a load has more than one pickup or delivery -
        // the API needs to accept and persist it for multi-stop overrides to round-trip.
        const pushRows = (rows, stopType) => {
          rows.forEach(row => {
            const start = combineDateAndTime(row.StartDate, row.StartTime);
            const end = combineDateAndTime(row.EndDate, row.EndTime);
            if (start && end) {
              payload.Appointments.push({
                stopType,
                locationID: row.locationID,
                appointmentStart: formatLocalDateTime(start),
                appointmentEnd: formatLocalDateTime(end)
              });
            }
          });
        };
        pushRows(this.laneSurf.PickupAppointments, 'Pickup');
        pushRows(this.laneSurf.DeliveryAppointments, 'Delivery');
      }

      return payload;
    }
  }
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-3:last-child {
  margin-bottom: 0;
}

.block {
  display: block;
}

label.block {
  font-weight: 500;
}

.section-heading {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-bg-page);
  margin: 1rem 0 1rem 0;
}

.w-full {
  width: 100%;
}

.field-group {
  display: flex;
  gap: 1rem;
}

.field-item {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  color: var(--text-bg-page);
}

.field-input {
  padding: 0.375rem;
  border: 1px solid var(--input-border);
  border-radius: 3px;
  font-size: 0.875rem;
}

.field-input-number :deep(.p-inputnumber-input) {
  width: 100%;
  padding: 0.375rem;
  border: 1px solid var(--input-border);
  border-radius: 3px;
  font-size: 0.875rem;
}

.field-input-number {
  width: 100%;
}

.field-input-number.error :deep(.p-inputnumber-input) {
  border-color: var(--badge-danger);
}

textarea.field-input {
  resize: vertical;
  font-family: inherit;
}

.equipment-row {
  margin-bottom: 0.5rem;
  align-items: center;
}

.equipment-type-col {
  flex: 2;
}

.equipment-length-col {
  flex: 1;
}

.equipment-delete-col {
  flex: 0 0 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-equipment-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-bg-page);
  padding: 0.375rem;
}

.delete-equipment-btn:hover {
  color: var(--badge-danger);
}

.add-equipment-btn {
  padding: 0.375rem 1rem;
  border: 1px solid var(--text-bg-page);
  color: var(--text-bg-page);
  border-radius: 3px;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
}

.add-equipment-btn:hover {
  border-color: var(--input-focus-border);
  color: var(--input-focus-border);
}

.error-dropdown {
  border-color: var(--badge-danger);
}

.field-input.error {
  border-color: var(--badge-danger);
}

.datetime-group {
  display: flex;
  gap: 0.5rem;
}

.datetime-group .field-input {
  flex: 1;
  min-width: 0;
}

.required {
  color: var(--badge-danger);
}

.override-checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-default);
  border-left: 3px solid var(--accent-primary);
  border-radius: 3px;
  background: var(--bg-secondary);
}

.override-checkbox-row :deep(.p-checkbox) {
  flex-shrink: 0;
  margin: 0;
}

.override-checkbox-row label {
  cursor: pointer;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
}

.appointment-section {
  border: 1px solid var(--border-default);
  border-radius: 3px;
  padding: 0.75rem;
  background: var(--bg-secondary);
}

.appointment-section-heading {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: var(--text-bg-page);
}

.appointment-section-heading.pickup-heading,
.appointment-section-heading.drop-heading {
  margin: -0.75rem -0.75rem 0.75rem -0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 3px 3px 0 0;
  color: var(--text-button);
}

.appointment-section-heading.pickup-heading {
  background: var(--button-tertiary-bg);
}

.appointment-section-heading.drop-heading {
  background: var(--button-secondary-bg);
}

.appointment-row + .appointment-row {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-default);
}

</style>
